import styled from '@emotion/styled';
import {
  type CurrentLocationResponse,
  type CurrentReplayQuery,
  type CurrentStarterInfo,
  RacemapAPIClient,
} from '@racemap/utilities/api-client';
import { HAIRSP } from '@racemap/utilities/consts/characters';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { OneSecondInMillis } from '@racemap/utilities/consts/time';
import { labelFormatDistance, labelFormatVelocity } from '@racemap/utilities/formattings/units';
import type { PointWithOffsetTimeNumber } from '@racemap/utilities/functions/codec';
import { currentEventTimes } from '@racemap/utilities/functions/event';
import {
  distanceUnitByUnitType,
  unitAwareDistance,
  unitAwareVelocity,
  velocityUnitByUnitType,
} from '@racemap/utilities/functions/units';
import { isRunningEvent } from '@racemap/utilities/functions/utils';
import { UnitType } from '@racemap/utilities/types/units';
import { Alert, Button, Card, InputNumber, Modal, Select, Space, Table, Tabs, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DateTime } from 'luxon';
import { type FC, useEffect, useMemo, useRef, useState } from 'react';
import { DatePicker } from 'src/components/BasicComponents/DateTimePicker';
import { IconDownload, IconOk, IconSwap } from 'src/components/BasicComponents/Icons';
import { XYAreachart } from 'src/components/Charts/XYAreachart';
import type { Series } from 'src/components/Charts/XYAreachart/XYAreachart';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useInterval } from 'src/lib/customHooks/useInterval';
import { useUnitType } from 'src/lib/customHooks/useUnitType';
import { useURLStates } from 'src/lib/customHooks/useURLStates';
import { MapCard } from './CurrentAPIAnalyticsMap';
import { CurrentAPIColorTable, MAP_PATH_DEFS } from './consts';
import { FollowButton } from './FollowButton';
import { TrackingIntervalBadge } from './TrackingIntervalBadge';
import type { CollectedSample, JsonValue } from './types';
import {
  analyseTrackingIntervals,
  classifyConstantProps,
  fetchRawPoints,
  getValueByPath,
  inferYAxisIndex,
  pathLeaf,
} from './utils';

const apiClient = RacemapAPIClient.fromWindowLocation();

// Route-distance conversion: API values are in meters, display in km / mi / nmi.
function metersToRouteDistance(meters: number, unitType: UnitType): number {
  if (unitType === UnitType.IMPERIAL) return meters / 1609.34;
  if (unitType === UnitType.NAUTICAL) return meters / 1852;
  return meters / 1000;
}

function isSpeedPath(path: string): boolean {
  const leaf = path.split('.').pop() ?? '';
  return leaf === 'speed' || leaf === 'speedSt';
}

// Stable axis options — defined outside component to avoid recreating on every render.
const CHART_X_AXIS_OPTION = { dataKey: 'time' as const, type: 'time' as const };
const CHART_Y_AXIS_OPTION = { dataKey: 'value' as const, type: 'value' as const, name: 'Value' };

export const CurrentAPIAnalytics: FC = () => {
  const currentEvent = useCurrentEvent();
  const initializedEventIdRef = useRef<string | null>(null);

  const [selectedStarterId, setSelectedStarterId] = useState<string | null>(null);
  const [selectedLeaderId, setSelectedLeaderId] = useState<string | null>(null);
  const [intervalSeconds, setIntervalSeconds] = useState<number>(10);
  const [recordingBeginAt, setRecordingBeginAt] = useState<string>('');
  const [recordingEndAt, setRecordingEndAt] = useState<string>('');
  const [avgSpeedWindowSeconds, setavgSpeedWindowSeconds] = useState<number>(300);
  const unitType = useUnitType();
  const [isCollecting, setIsCollecting] = useState(false);
  // Follow mode on/off — internal only, not persisted to the URL.
  const [follow, setFollow] = useState(false);
  // True only while a single Follow tick is in flight — drives the button's busy animation.
  const [isFollowing, setIsFollowing] = useState(false);
  const [collectionError, setCollectionError] = useState<string | null>(null);
  const [collectedResponses, setCollectedResponses] = useState<Array<CurrentLocationResponse>>([]);
  const [numericChartLegendSelection, setNumericChartLegendSelection] = useState<
    Record<string, boolean>
  >({});
  const [rawStarterPoints, setRawStarterPoints] = useState<Array<PointWithOffsetTimeNumber>>([]);
  const [rawLeaderPoints, setRawLeaderPoints] = useState<Array<PointWithOffsetTimeNumber>>([]);
  const [hoveredChartTimestamp, setHoveredChartTimestamp] = useState<number | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  const [urlParams, setUrlParams] = useURLStates({
    starterId: { defaultValue: null as string | null, paramType: 'string' },
    refStarterId: { defaultValue: null as string | null, paramType: 'string' },
    beginAt: { defaultValue: null as string | null, paramType: 'string' },
    endAt: { defaultValue: null as string | null, paramType: 'string' },
    interval: { defaultValue: null as number | null, paramType: 'number' },
    avgSpeedWindow: { defaultValue: null as number | null, paramType: 'number' },
    autoFetch: { defaultValue: false, paramType: 'boolean' },
  });

  if (currentEvent == null) return null;

  // Follow mode (live continuous fetching) is only offered for live or repeating events.
  const canFollow = isRunningEvent(currentEvent);

  useEffect(() => {
    if (initializedEventIdRef.current === currentEvent.id) return;

    const [startMillis, endMillis] = currentEventTimes(currentEvent);
    const now = Date.now();
    const initialEndMillis = Math.min(endMillis ?? now, now);

    const computedBegin = new Date(startMillis ?? Date.parse(currentEvent.startTime)).toISOString();
    const computedEnd = new Date(initialEndMillis).toISOString();
    setRecordingBeginAt(urlParams.beginAt ?? computedBegin);
    setRecordingEndAt(urlParams.endAt ?? computedEnd);

    if (urlParams.starterId != null && currentEvent.starters?.has(urlParams.starterId)) {
      setSelectedStarterId(urlParams.starterId);
    }
    if (urlParams.refStarterId != null && currentEvent.starters?.has(urlParams.refStarterId)) {
      setSelectedLeaderId(urlParams.refStarterId);
    }
    if (urlParams.interval != null) {
      setIntervalSeconds(urlParams.interval);
    }
    if (urlParams.avgSpeedWindow != null) {
      setavgSpeedWindowSeconds(urlParams.avgSpeedWindow);
    }

    setIsInitialized(true);
    initializedEventIdRef.current = currentEvent.id;
  }, [
    currentEvent,
    urlParams.starterId,
    urlParams.refStarterId,
    urlParams.beginAt,
    urlParams.endAt,
    urlParams.interval,
    urlParams.avgSpeedWindow,
  ]);

  const selectedParticipant =
    selectedStarterId != null ? (currentEvent.starters?.get(selectedStarterId) ?? null) : null;

  const participantOptions = useMemo(
    () =>
      Array.from(currentEvent.starters?.values() ?? [])
        .map((starter) => ({
          value: starter.id,
          label: `${starter.startNumber ?? '-'} - ${starter.name || starter.id}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [currentEvent],
  );

  const collectedSamples: Array<CollectedSample> = useMemo(
    () =>
      collectedResponses.map((entry, i) => {
        const resultTime: string = entry.resultTime;
        const starters = Array.isArray(entry?.starters) ? entry.starters : [];
        const starterInfo = starters.find((s) => s.id === selectedStarterId) ?? starters[0] ?? null;
        const refInfo = starters.find((s) => s.current.isRef) ?? null;
        const sampleRoot = {
          participant: selectedParticipant,
          current: starterInfo?.current,
          device: starterInfo?.device,
        };
        const values: Record<string, JsonValue | undefined> = {};
        for (const { path } of MAP_PATH_DEFS) {
          values[path] = getValueByPath(sampleRoot, path);
        }
        // Always store ref position for map hover, regardless of selected properties.
        values['ref.lat'] = refInfo?.current.lat ?? null;
        values['ref.lng'] = refInfo?.current.lng ?? null;
        values['ref.latSt'] = refInfo?.current.latSt ?? null;
        values['ref.lngSt'] = refInfo?.current.lngSt ?? null;
        return {
          key: `${resultTime}-${i}`,
          resultTime,
          values,
        };
      }),
    [collectedResponses, selectedStarterId, selectedParticipant],
  );

  const numericSelectedPropertyPaths = useMemo(
    () => MAP_PATH_DEFS.filter((d) => !d.hidden && d.type === 'number').map((d) => d.path),
    [],
  );

  // Non-numeric paths that have the same value across all samples → listed in Constants tab.
  const constantProps = useMemo(
    () =>
      classifyConstantProps(
        collectedSamples,
        MAP_PATH_DEFS.filter((d) => !d.hidden).map((d) => d.path),
      ),
    [collectedSamples],
  );

  // Non-numeric paths that changed across samples — shown as toggleable tags above the table.
  const changingNonNumericPaths = useMemo(
    () =>
      MAP_PATH_DEFS.filter(
        (d) => !d.hidden && d.type !== 'number' && !(d.path in constantProps),
      ).map((d) => d.path),
    [constantProps],
  );

  const [selectedNonNumericPaths, setSelectedNonNumericPaths] = useState<Set<string>>(new Set());

  // When the set of changing paths updates (i.e. after a new fetch), reset selection to none.
  useEffect(() => {
    setSelectedNonNumericPaths(new Set());
  }, [changingNonNumericPaths]);

  // Columns for the main samples table:
  // - numeric paths currently visible in the chart legend
  // - non-constant non-numeric paths the user has toggled on
  const visibleTablePaths = useMemo(() => {
    const numericVisible = numericSelectedPropertyPaths.filter(
      (path) => numericChartLegendSelection[path] === true,
    );
    const nonNumericVisible = changingNonNumericPaths.filter((path) =>
      selectedNonNumericPaths.has(path),
    );
    return [...numericVisible, ...nonNumericVisible];
  }, [
    numericSelectedPropertyPaths,
    numericChartLegendSelection,
    changingNonNumericPaths,
    selectedNonNumericPaths,
  ]);

  const numericSeries: Series = useMemo(
    () =>
      numericSelectedPropertyPaths.map((path) => {
        const leaf = path.startsWith('current.') ? path.slice('current.'.length) : null;
        const color =
          leaf != null && leaf in CurrentAPIColorTable
            ? CurrentAPIColorTable[leaf as keyof typeof CurrentAPIColorTable]
            : undefined;
        return {
          name: path,
          type: 'line',
          showSymbol: true,
          symbolSize: 4,
          lineStyle: { width: 0 },
          yAxisIndex: inferYAxisIndex(path),
          ...(color != null ? { color, itemStyle: { color } } : {}),
          data: collectedSamples
            .map((sample) => {
              const value = sample.values[path];
              if (typeof value !== 'number') return null;
              const yIdx = inferYAxisIndex(path);
              const converted = isSpeedPath(path)
                ? unitAwareVelocity(value * 3.6, unitType)
                : yIdx === 1
                  ? metersToRouteDistance(value, unitType)
                  : value;
              return [new Date(sample.resultTime).getTime(), converted];
            })
            .filter((entry): entry is [number, number] => entry != null),
        };
      }),
    [numericSelectedPropertyPaths, collectedSamples, unitType],
  );

  const chartSeriesNames = useMemo(() => {
    if (!Array.isArray(numericSeries)) return [];
    return numericSeries
      .map((entry) => (entry != null && typeof entry === 'object' ? entry.name : undefined))
      .filter((name): name is string => typeof name === 'string');
  }, [numericSeries]);

  useEffect(() => {
    setNumericChartLegendSelection((previous) => {
      const next: Record<string, boolean> = {};
      for (const name of chartSeriesNames) {
        next[name] =
          previous[name] ??
          MAP_PATH_DEFS.filter((d) => d.defaultVisible)
            .map((d) => d.path)
            .includes(name);
      }
      return next;
    });
  }, [chartSeriesNames]);

  useEffect(() => {
    if (selectedStarterId == null) {
      setRawStarterPoints([]);
      return;
    }

    const beginMillis = new Date(recordingBeginAt).getTime();
    const endMillis = new Date(recordingEndAt).getTime();
    if (!Number.isFinite(beginMillis) || !Number.isFinite(endMillis) || beginMillis > endMillis) {
      setRawStarterPoints([]);
      return;
    }

    let disposed = false;
    fetchRawPoints(currentEvent.id, selectedStarterId, beginMillis, endMillis)
      .then((track) => {
        if (!disposed) setRawStarterPoints(track);
      })
      .catch(() => {
        if (!disposed) setRawStarterPoints([]);
      });
    return () => {
      disposed = true;
    };
  }, [currentEvent.id, selectedStarterId, recordingBeginAt, recordingEndAt]);

  useEffect(() => {
    if (selectedLeaderId == null) {
      setRawLeaderPoints([]);
      return;
    }

    const beginMillis = new Date(recordingBeginAt).getTime();
    const endMillis = new Date(recordingEndAt).getTime();
    if (!Number.isFinite(beginMillis) || !Number.isFinite(endMillis) || beginMillis > endMillis) {
      setRawLeaderPoints([]);
      return;
    }

    let disposed = false;
    fetchRawPoints(currentEvent.id, selectedLeaderId, beginMillis, endMillis)
      .then((track) => {
        if (!disposed) setRawLeaderPoints(track);
      })
      .catch(() => {
        if (!disposed) setRawLeaderPoints([]);
      });
    return () => {
      disposed = true;
    };
  }, [currentEvent.id, selectedLeaderId, recordingBeginAt, recordingEndAt]);

  const starterTrackingStats = useMemo(
    () => analyseTrackingIntervals(rawStarterPoints),
    [rawStarterPoints],
  );
  const leaderTrackingStats = useMemo(
    () => analyseTrackingIntervals(rawLeaderPoints),
    [rawLeaderPoints],
  );

  // Shared by the one-shot Fetch and the incremental Follow tick: build the query + call the API.
  const fetchReplayRange = (
    startMillis: number,
    endMillis: number,
  ): Promise<Array<CurrentLocationResponse>> => {
    const replayQuery: CurrentReplayQuery = {
      selectedForReplayId: selectedStarterId as string,
      start: new Date(startMillis).toISOString(),
      end: new Date(endMillis).toISOString(),
      avgSpeedWindow: avgSpeedWindowSeconds,
      intervalSeconds,
      participantId: selectedLeaderId ?? undefined,
    };
    return apiClient.getCurrentReplay(currentEvent.id, replayQuery);
  };

  const handleStartCollectingSamples = async () => {
    if (selectedStarterId == null) {
      setCollectionError('Please select a participant first.');
      return;
    }

    const eventStartMillis = new Date(currentEvent.startTime).getTime();
    const beginMillis = new Date(recordingBeginAt).getTime();
    const endMillis = new Date(recordingEndAt).getTime();
    if (Number.isNaN(eventStartMillis) || Number.isNaN(beginMillis) || Number.isNaN(endMillis)) {
      setCollectionError('Begin or end timestamp is invalid.');
      return;
    }

    if (beginMillis < eventStartMillis) {
      setCollectionError('Begin must be at or after event start time.');
      return;
    }

    if (beginMillis > endMillis) {
      setCollectionError('Begin must be earlier than or equal to End.');
      return;
    }

    setCollectionError(null);
    setCollectedResponses([]);
    setIsCollecting(true);

    try {
      const results = await fetchReplayRange(beginMillis, endMillis);
      setCollectedResponses(results);
      // A successful full fetch on a live/repeating event hands off to Follow mode.
      if (canFollow && results.length > 0) setFollow(true);
    } catch (error) {
      setCollectionError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      setIsCollecting(false);
    }
  };

  // One Follow tick: fetch the gap from the last collected datapoint to "now" and append it.
  const runFollowTick = async () => {
    if (selectedStarterId == null) return;

    const last = collectedResponses.at(-1)?.resultTime;
    const startMillis = last != null ? Date.parse(last) : Date.parse(recordingEndAt);
    const [, endBoundMillis] = currentEventTimes(currentEvent); // fresh, repeating-aware
    const targetEndMillis = Math.min(Date.now(), endBoundMillis ?? Date.now());

    if (!(targetEndMillis > startMillis)) {
      // Caught up. For a finished live event there is nothing more to fetch — stop following.
      if (!currentEvent.isRepeating) setFollow(false);
      return;
    }

    setIsFollowing(true);
    try {
      const fresh = (await fetchReplayRange(startMillis, targetEndMillis)).filter(
        (r) => Date.parse(r.resultTime) > startMillis, // drop the boundary duplicate
      );
      if (fresh.length > 0) {
        setCollectedResponses((prev) => [...prev, ...fresh]);
        // Advance the End field to the newest collected datapoint.
        setRecordingEndAt(new Date(Date.parse(fresh[fresh.length - 1].resultTime)).toISOString());
      }
      // Live event finished: the final gap is now closed, so stop following.
      if (!currentEvent.isRepeating && endBoundMillis != null && Date.now() >= endBoundMillis) {
        setFollow(false);
      }
    } catch (error) {
      setCollectionError(error instanceof Error ? error.message : 'Follow fetch failed');
    } finally {
      setIsFollowing(false);
    }
  };

  useEffect(() => {
    if (!isInitialized || !urlParams.autoFetch) return;
    void handleStartCollectingSamples();
  }, [isInitialized, urlParams.autoFetch]);

  // Poll loop for Follow mode — fixed 10s cadence, re-reads the latest closure each render.
  const followDelayMs = 10 * OneSecondInMillis;
  // Timestamp marking the start of the current wait between ticks; drives FollowButton's fill bar.
  const followCycleStartRef = useRef(0);
  useInterval(async () => {
    if (!follow) return;
    await runFollowTick();
    // The next wait (setTimeout in useInterval) begins now — reset the countdown bar to match.
    followCycleStartRef.current = Date.now();
  }, followDelayMs);

  const beginDateTime = DateTime.fromISO(recordingBeginAt);
  const endDateTime = DateTime.fromISO(recordingEndAt);

  const handleDownloadRawPoints = (points: Array<PointWithOffsetTimeNumber>, id: string | null) => {
    if (points.length === 0) return;
    const data = points.map((p) => ({ lat: p.lat, lng: p.lng, time: p.time, offset: p.offset }));
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${id ?? 'unknown'}.raw.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Stable event handlers — no inline objects/functions so ReactECharts doesn't see a changed
  // option on every render (which would call setOption and reset the zoom state).
  const chartOnEvents = useMemo(
    () => ({
      legendselectchanged: (payload: unknown) => {
        const selected =
          payload != null && typeof payload === 'object'
            ? (payload as { selected?: Record<string, boolean> }).selected
            : undefined;
        if (selected != null) {
          setNumericChartLegendSelection(selected);
        }
      },
      updateaxispointer: (payload: unknown) => {
        const p = payload as
          | { axesInfo?: Array<{ axisDim?: string; value?: number }> }
          | null
          | undefined;
        const xVal = p?.axesInfo?.find((a) => a.axisDim === 'x')?.value ?? null;
        if (typeof xVal === 'number') setHoveredChartTimestamp(xVal);
      },
    }),
    [],
  );

  return (
    <>
      <IntroText>
        Fetch a time-series of Current-API snapshots for any participant and visualise how computed
        properties (speed, distance, gaps, …) evolve over the course of a race. Select a participant
        and optionally a reference starter, choose a time window, then hit <strong>Fetch</strong>.
        Numeric properties appear as a zoomable chart; string/boolean properties can be toggled as
        table columns. Constant properties (same value across all samples) are listed separately.
      </IntroText>

      <Card styles={configCardStyles}>
        {/* Input groups — equal width, wrap on narrow screens */}
        <InputsRow>
          <InputGroup>
            <TrackingIntervalBadge stats={starterTrackingStats} />
            <CompactInputRow>
              <Button
                disabled
                style={{
                  borderLeft: `3px solid ${CurrentAPIColorTable.participant}`,
                }}
              >
                Starter
              </Button>
              <Select
                style={{ flex: 1, minWidth: 0 }}
                placeholder="Select a participant"
                value={selectedStarterId ?? undefined}
                onChange={(value) => {
                  const id = (value as string | undefined) ?? null;
                  setSelectedStarterId(id);
                  setUrlParams({ starterId: id });
                }}
                options={participantOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={follow}
              />
              <Button
                icon={<IconDownload />}
                title="Download raw points as JSON"
                disabled={rawStarterPoints.length === 0}
                onClick={() => handleDownloadRawPoints(rawStarterPoints, selectedStarterId)}
              />
            </CompactInputRow>
          </InputGroup>
          <Button
            icon={<IconSwap />}
            title="Swap starter and leader"
            disabled={follow || selectedStarterId == null || selectedLeaderId == null}
            onClick={() => {
              const tmp = selectedStarterId;
              setSelectedStarterId(selectedLeaderId);
              setSelectedLeaderId(tmp);
              setUrlParams({ starterId: selectedLeaderId, refStarterId: tmp });
            }}
            style={{ alignSelf: 'flex-end', marginBottom: 1 }}
          />
          <InputGroup>
            <TrackingIntervalBadge stats={leaderTrackingStats} />
            <CompactInputRow>
              <Button
                disabled
                style={{
                  borderLeft: `3px solid ${CurrentAPIColorTable.leader}`,
                }}
              >
                Leader
              </Button>
              <Select
                style={{ flex: 1, minWidth: 0 }}
                placeholder="Select reference starter"
                value={selectedLeaderId ?? undefined}
                onChange={(value) => {
                  const id = (value as string | undefined) ?? null;
                  setSelectedLeaderId(id);
                  setUrlParams({ refStarterId: id });
                }}
                options={participantOptions}
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={follow}
              />
              <Button
                icon={<IconDownload />}
                title="Download raw points of reference as JSON"
                disabled={rawLeaderPoints.length === 0}
                onClick={() => handleDownloadRawPoints(rawLeaderPoints, selectedLeaderId)}
              />
            </CompactInputRow>
          </InputGroup>
          <InputGroup>
            <CompactInputRow>
              <Button disabled>Begin</Button>
              <DatePicker
                value={beginDateTime.isValid ? beginDateTime : null}
                onChange={(value) => {
                  if (value != null) {
                    const iso = (value as DateTime).toISO();
                    if (iso != null) {
                      setRecordingBeginAt(iso);
                      setUrlParams({ beginAt: iso });
                    }
                  } else {
                    setUrlParams({ beginAt: null });
                  }
                }}
                showTime
                disabled={follow}
              />
            </CompactInputRow>
          </InputGroup>
          <InputGroup>
            <CompactInputRow>
              <Button disabled>End</Button>
              <DatePicker
                value={endDateTime.isValid ? endDateTime : null}
                onChange={(value) => {
                  if (value != null) {
                    const iso = (value as DateTime).toISO();
                    if (iso != null) {
                      setRecordingEndAt(iso);
                      setUrlParams({ endAt: iso });
                    }
                  } else {
                    setUrlParams({ endAt: null });
                  }
                }}
                showTime
                disabled={follow}
              />
            </CompactInputRow>
          </InputGroup>
          <InputGroup>
            <CompactInputRow>
              <Button disabled>Interval (s)</Button>
              <InputNumber
                min={1}
                value={intervalSeconds}
                onChange={(value) => {
                  setIntervalSeconds(value ?? 10);
                  setUrlParams({ interval: value ?? 10 });
                }}
                style={{ flex: 1, minWidth: 0 }}
                disabled={follow}
              />
            </CompactInputRow>
          </InputGroup>
          <InputGroup>
            <CompactInputRow>
              <Button disabled>Avg speed window (s)</Button>
              <InputNumber
                min={1}
                value={avgSpeedWindowSeconds}
                onChange={(value) => {
                  setavgSpeedWindowSeconds(value ?? 300);
                  setUrlParams({ avgSpeedWindow: value ?? 300 });
                }}
                style={{ flex: 1, minWidth: 0 }}
                disabled={follow}
              />
            </CompactInputRow>
          </InputGroup>
          <Button
            type="primary"
            loading={isCollecting}
            disabled={follow}
            onClick={handleStartCollectingSamples}
          >
            Fetch
          </Button>
          {(canFollow || follow) && (
            <FollowButton
              active={follow}
              durationMs={followDelayMs}
              cycleStartRef={followCycleStartRef}
              loading={isFollowing}
              icon={follow && !isFollowing ? <IconOk /> : undefined}
              disabled={collectedResponses.length === 0}
              onClick={() => setFollow((prev) => !prev)}
            >
              Follow
            </FollowButton>
          )}
        </InputsRow>
        {follow && (
          <FollowLockNotice
            type="info"
            showIcon
            title="Inputs are locked while Follow is on. Turn off Follow to edit the query."
          />
        )}
        {collectionError != null && <ErrorText>{collectionError}</ErrorText>}
      </Card>

      <MapAndPlots>
        <MapCard
          currentEvent={currentEvent}
          rawStarterPoints={rawStarterPoints}
          rawLeaderPoints={rawLeaderPoints}
          collectedSamples={collectedSamples}
          hoveredChartTimestamp={hoveredChartTimestamp}
          collectedResponses={collectedResponses}
          selectedStarterId={selectedStarterId}
          selectedLeaderId={selectedLeaderId}
          avgSpeedWindowSeconds={avgSpeedWindowSeconds}
          unitType={unitType}
        />
        <PlotsCard
          chartSeriesWithRawPoints={numericSeries}
          chartOnEvents={chartOnEvents}
          numericChartLegendSelection={numericChartLegendSelection}
          unitType={unitType}
        />
      </MapAndPlots>

      <TableCard
        collectedSamples={collectedSamples}
        constantProps={constantProps}
        changingNonNumericPaths={changingNonNumericPaths}
        selectedNonNumericPaths={selectedNonNumericPaths}
        unitType={unitType}
        onTogglePath={(path) =>
          setSelectedNonNumericPaths((prev) => {
            const next = new Set(prev);
            if (next.has(path)) next.delete(path);
            else next.add(path);
            return next;
          })
        }
        collectedResponses={collectedResponses}
        visibleTablePaths={visibleTablePaths}
        selectedStarterId={selectedStarterId}
      />
    </>
  );
};

// ─── Tab sub-components ──────────────────────────────────────────────────────

interface SamplesTabProps {
  changingNonNumericPaths: Array<string>;
  selectedNonNumericPaths: Set<string>;
  onTogglePath: (path: string) => void;
  collectedSamples: Array<CollectedSample>;
  sampleColumns: ColumnsType<CollectedSample>;
}

const SamplesTab: FC<SamplesTabProps> = ({
  changingNonNumericPaths,
  selectedNonNumericPaths,
  onTogglePath,
  collectedSamples,
  sampleColumns,
}) => (
  <>
    <p>
      These properties are strings and changed over time in the collected samples. You can click on
      them to toggle their visibility as columns in the table (they are not shown by default to
      avoid overwhelming you with too many columns, but you can show as many as you want).
    </p>
    {changingNonNumericPaths.length > 0 && (
      <Space style={{ marginBottom: 10 }} wrap>
        {changingNonNumericPaths.map((path) => (
          <Tag
            key={path}
            color={selectedNonNumericPaths.has(path) ? 'blue' : 'default'}
            style={{ cursor: 'pointer', userSelect: 'none' }}
            title={path}
            onClick={() => onTogglePath(path)}
          >
            {pathLeaf(path)}
          </Tag>
        ))}
      </Space>
    )}
    <Table
      rowKey="key"
      size="small"
      dataSource={collectedSamples}
      columns={sampleColumns}
      pagination={false}
      virtual
      scroll={{ x: 1200, y: 420 }}
    />
  </>
);

interface ConstantsTabProps {
  constantProps: Record<string, string>;
}

const ConstantsTab: FC<ConstantsTabProps> = ({ constantProps }) => (
  <>
    This properties did not change over the collected samples and are likely constant for the entire
    event (but this is not guaranteed, as we only check the collected time range).
    <Table
      size="small"
      dataSource={Object.entries(constantProps).map(([path, value]) => ({
        key: path,
        path,
        value,
      }))}
      columns={[
        {
          title: 'Property',
          dataIndex: 'path',
          key: 'path.end',
          width: 200,
          render: (p: string) => <span title={p}>{pathLeaf(p)}</span>,
        },
        { title: 'Value', dataIndex: 'value', key: 'value' },
        {
          title: 'Path',
          dataIndex: 'path',
          key: 'path',
          render: (p: string) => <span title={p}>{p}</span>,
        },
      ]}
      pagination={false}
      scroll={{ y: 420 }}
    />
  </>
);

// ─── Card sub-components ─────────────────────────────────────────────────────

type DebugModalData = {
  participant: CurrentStarterInfo | null;
  ref: CurrentStarterInfo | null;
};

interface TableCardProps {
  collectedSamples: Array<CollectedSample>;
  collectedResponses: Array<CurrentLocationResponse>;
  constantProps: Record<string, string>;
  changingNonNumericPaths: Array<string>;
  selectedNonNumericPaths: Set<string>;
  onTogglePath: (path: string) => void;
  visibleTablePaths: Array<string>;
  selectedStarterId: string | null;
  unitType: UnitType;
}

const TableCard: FC<TableCardProps> = ({
  collectedSamples,
  collectedResponses,
  constantProps,
  changingNonNumericPaths,
  selectedNonNumericPaths,
  onTogglePath,
  visibleTablePaths,
  selectedStarterId,
  unitType,
}) => {
  const [debugModalTimestamp, setDebugModalTimestamp] = useState<string | null>(null);
  const [debugModalData, setDebugModalData] = useState<DebugModalData | null>(null);

  const handleTimestampClick = (resultTime: string) => {
    const entry = collectedResponses.find((r) => r.resultTime === resultTime) ?? null;
    const starters = entry?.starters ?? [];
    const participant = starters.find((s) => s.id === selectedStarterId) ?? starters[0] ?? null;
    const ref = starters.find((s) => s.current.isRef && s.id !== participant?.id) ?? null;
    setDebugModalTimestamp(resultTime);
    setDebugModalData({ participant, ref });
  };

  const sampleColumns: ColumnsType<CollectedSample> = [
    {
      title: 'Result Time',
      dataIndex: 'resultTime',
      key: 'resultTime',
      width: 220,
      render: (resultTime: string) => (
        <TimestampLink onClick={() => void handleTimestampClick(resultTime)}>
          {resultTime}
        </TimestampLink>
      ),
    },
    ...visibleTablePaths.map((path) => ({
      title: pathLeaf(path),
      key: path,
      onHeaderCell: () => ({ title: path }),
      render: (_value: unknown, record: CollectedSample) => {
        const value = record.values[path];
        if (value == null) return '-';
        if (typeof value === 'object') return JSON.stringify(value);
        if (typeof value === 'number') {
          if (isSpeedPath(path)) {
            return `${labelFormatVelocity(unitAwareVelocity(3.6 * value, unitType), unitType)}`;
          }
          if (inferYAxisIndex(path) === 1) {
            return `${labelFormatDistance(unitAwareDistance(value, unitType), unitType)}`;
          }
        }
        return String(value);
      },
    })),
  ];

  return (
    <>
      <Card>
        <Tabs
          defaultActiveKey="samples"
          items={[
            {
              key: 'samples',
              label: `Samples (${collectedSamples.length})`,
              children: (
                <SamplesTab
                  changingNonNumericPaths={changingNonNumericPaths}
                  selectedNonNumericPaths={selectedNonNumericPaths}
                  onTogglePath={onTogglePath}
                  collectedSamples={collectedSamples}
                  sampleColumns={sampleColumns}
                />
              ),
            },
            {
              key: 'constants',
              label: `Constants (${Object.keys(constantProps).length})`,
              children: <ConstantsTab constantProps={constantProps} />,
            },
          ]}
        />
      </Card>

      <Modal
        open={debugModalTimestamp != null}
        title={`Current API — ${debugModalTimestamp ?? ''}`}
        onCancel={() => setDebugModalTimestamp(null)}
        footer={null}
        width={700}
      >
        <JsonPre>{JSON.stringify(debugModalData, null, 2)}</JsonPre>
      </Modal>
    </>
  );
};

// ─── PlotsCard ────────────────────────────────────────────────────────────────

type XYAreachartProps = Parameters<typeof XYAreachart>[0];

interface PlotsCardProps {
  chartSeriesWithRawPoints: Series;
  chartOnEvents: XYAreachartProps['onEvents'];
  numericChartLegendSelection: Record<string, boolean>;
  unitType: UnitType;
}

const PlotsCard: FC<PlotsCardProps> = ({
  chartSeriesWithRawPoints,
  chartOnEvents,
  numericChartLegendSelection,
  unitType,
}) => {
  // Which y-axis indices have at least one currently-visible series.
  const activeYAxisIndices = useMemo(() => {
    const active = new Set<number>();
    if (!Array.isArray(chartSeriesWithRawPoints)) return active;
    for (const s of chartSeriesWithRawPoints) {
      if (s == null || typeof s !== 'object') continue;
      const name = (s as { name?: string }).name;
      const idx = (s as { yAxisIndex?: number }).yAxisIndex ?? 0;
      // undefined in the selection map means the series hasn't been toggled off yet → visible.
      const visible = name != null ? numericChartLegendSelection[name] !== false : true;
      if (visible) active.add(idx);
    }
    return active;
  }, [chartSeriesWithRawPoints, numericChartLegendSelection]);

  // Pack visible right-side axes tightly: reassign offsets so no empty gaps appear.
  const rightAxisLayout = useMemo(() => {
    const rightIndices = [1, 2, 3] as const;
    let currentOffset = 0;
    const axes = rightIndices.map((idx) => {
      const show = activeYAxisIndices.has(idx);
      const offset = show ? currentOffset : 0;
      if (show) currentOffset += 75;
      return { show, offset };
    });
    // gridRight = offset of rightmost visible axis + space for its tick labels (~65 px).
    const gridRight = currentOffset === 0 ? 10 : currentOffset - 75 + 65;
    return { axes, gridRight };
  }, [activeYAxisIndices]);

  const numericChartOptions = useMemo(
    () => ({
      legend: {
        show: true,
        type: 'scroll' as const,
        selected: numericChartLegendSelection,
        formatter: (name: string) => pathLeaf(name),
        tooltip: { show: true, formatter: (params: { name?: string }) => params.name ?? '' },
      },
      tooltip: {
        trigger: 'axis' as const,
        formatter: (payload: unknown) => {
          const rows = Array.isArray(payload) ? payload : [payload];
          if (rows.length === 0) return '';

          const first = rows[0] as { axisValueLabel?: string } | undefined;
          const header = first?.axisValueLabel || '';

          const lines = rows.map((row) => {
            const p = row as {
              marker?: string;
              seriesName?: string;
              value?: number | [number, number] | string;
            };
            const marker = p.marker || '';
            const name = p.seriesName || '-';

            const rawValue = Array.isArray(p.value) ? p.value[1] : p.value;

            let unit = '';
            if (isSpeedPath(name)) unit = `${HAIRSP}${velocityUnitByUnitType(unitType)}`;
            else if (inferYAxisIndex(name) === 1)
              unit = `${HAIRSP}${distanceUnitByUnitType(unitType)}`;
            else if (inferYAxisIndex(name) === 2) unit = `${HAIRSP}s`;

            const displayValue =
              typeof rawValue === 'number' ? `${rawValue.toFixed(2)}${unit}` : (rawValue ?? '-');

            return `${marker}${pathLeaf(name)}: ${displayValue}`;
          });

          return [header, ...lines].join('<br/>');
        },
      },
      yAxis: [
        {
          type: 'value' as const,
          name: `Speed (${velocityUnitByUnitType(unitType)})`,
          position: 'left' as const,
          show: activeYAxisIndices.has(0),
          nameRotate: 90,
          nameLocation: 'middle' as const,
          nameGap: 45,
        },
        {
          type: 'value' as const,
          name: `Distance (${distanceUnitByUnitType(unitType)})`,
          position: 'right' as const,
          show: rightAxisLayout.axes[0].show,
          offset: rightAxisLayout.axes[0].offset,
          nameRotate: 90,
          nameLocation: 'middle' as const,
          nameGap: 45,
        },
        {
          type: 'value' as const,
          name: 'Duration (s)',
          position: 'right' as const,
          show: rightAxisLayout.axes[1].show,
          offset: rightAxisLayout.axes[1].offset,
          nameRotate: 90,
          nameLocation: 'middle' as const,
          nameGap: 45,
        },
        {
          type: 'value' as const,
          name: 'Ratio (%)',
          position: 'right' as const,
          show: rightAxisLayout.axes[2].show,
          offset: rightAxisLayout.axes[2].offset,
          nameRotate: 90,
          nameLocation: 'middle' as const,
          nameGap: 45,
        },
      ],
      grid: { top: 40, left: 60, right: rightAxisLayout.gridRight, bottom: 70, show: true },
      dataZoom: [
        { type: 'inside' as const, filterMode: 'empty' as const },
        { type: 'slider' as const, filterMode: 'empty' as const },
      ],
    }),
    [numericChartLegendSelection, activeYAxisIndices, rightAxisLayout, unitType],
  );

  return (
    <Card>
      <ChartLabel>Metrics over time</ChartLabel>
      <div style={{ height: 460 }}>
        <XYAreachart
          series={chartSeriesWithRawPoints}
          xAxisOption={CHART_X_AXIS_OPTION}
          yAxisOption={CHART_Y_AXIS_OPTION}
          onEvents={chartOnEvents}
          chartOptions={numericChartOptions}
        />
      </div>
    </Card>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

const IntroText = styled.p`
  margin: 0;
  color: rgba(0, 0, 0, 0.55);
  font-size: 13px;
  line-height: 1.6;
`;

const configCardStyles = {
  body: { display: 'flex', flexDirection: 'column', gap: 10 },
} as const;

// Wraps all equal-width input groups; wraps to new lines on narrow screens.
const InputsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 8px;
`;

// One labeled input; all share the same fixed flex-basis so they wrap one at a time.
const InputGroup = styled.div`
  flex: 1 1 220px;
  max-width: 340px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const CompactInputRow = styled(Space.Compact)`
  width: 100%;
  .ant-picker {
    flex: 1;
    min-width: 0;
  }
`;

const ErrorText = styled.div`
  color: ${RacemapColors.DarkRed};
`;

// Slim hint shown while Follow is active, explaining why the inputs are locked.
const FollowLockNotice = styled(Alert)`
  width: 100%;
  margin-top: 8px;
`;

const TimestampLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: ${RacemapColors.PaleBlue};
  cursor: pointer;
  font-size: inherit;
  text-decoration: underline;
  text-underline-offset: 2px;

  &:hover {
    color: ${RacemapColors.DarkBlue};
  }
`;

const JsonPre = styled.pre`
  background: #141414;
  color: #d4d4d4;
  padding: 16px;
  border-radius: 6px;
  font-size: 12px;
  line-height: 1.5;
  overflow: auto;
  max-height: 60vh;
  margin: 0;
`;

const MapAndPlots = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChartLabel = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.45);
  margin-bottom: 4px;
`;
